import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';
import { SITE_URLS } from '@constants';
import { FinancingLogo } from '../FinancingLogo';
import livroReclamacoesLogo from '@assets/images/logos/livro-reclamacoes.svg';
import styles from './Footer.module.css';
import { useSettings } from '@hooks/useSettings';
import { useGetFooter } from '@services/useGetFooter';

export interface FooterProps {
    mode: 'short' | 'normal';
}

export const Footer: FC<FooterProps> = ({ mode }) => {
    const { allSettings } = useData();
    const { useGrayscaleTheme } = useThemes();
    const { t } = useTranslation();
    const { getPage } = useSettings();
    const { footer, request: fetchFooter } = useGetFooter();

    const translations = {
        allRightsReserved: t('footer-component.all-rights-reserved'),
        livroReclamacoesAlt: t('footer-component.livro-reclamacoes.alt'),
        refunds: t('policy-component.refunds.title'),
        termsConditions: t('policy-component.terms-and-conditions.title'),
        privacy: t('policy-component.privacy-policy.title'),
        cookies: t('policy-component.cookies-policy.title'),
    };

    // Data from API
    const data = allSettings?.data;

    const logoItem = data?.layouts?.find((item) => item.name === 'logo');
    const logoSrc = logoItem?.value;

    const footerLogoSrc = footer?.content_footer_logo_text || logoSrc;

    const fundingLogoSrc = footer?.content_footer_funding_logo || null;
    const complaintsBookLogoSrc = footer?.content_footer_complaints_book_logo || livroReclamacoesLogo;

    const footerAddress = footer?.content_footer_address || null;
    const footerEmail = footer?.content_footer_email || null;
    const footerPhone = footer?.content_footer_phone || null;
    const footerCallInfo = footer?.content_footer_call_info || null;

    const footerAddressParts = footerAddress ? footerAddress.split(',') : [];

    const socialNetworks =
        data?.social_networks ||
        (footer
            ? [
                  footer.content_footer_social_facebook && {
                      url: footer.content_footer_social_facebook,
                      icon: 'fa-brands fa-facebook-f',
                  },
                  footer.content_footer_social_instagram && {
                      url: footer.content_footer_social_instagram,
                      icon: 'fa-brands fa-instagram',
                  },
                  footer.content_footer_social_youtube && {
                      url: footer.content_footer_social_youtube,
                      icon: 'fa-brands fa-youtube',
                  },
                  footer.content_footer_social_x && {
                      url: footer.content_footer_social_x,
                      icon: 'fa-brands fa-x-twitter',
                  },
                  footer.content_footer_social_tiktok && {
                      url: footer.content_footer_social_tiktok,
                      icon: 'fa-brands fa-tiktok',
                  },
              ].filter(Boolean)
            : undefined);

    const footerNavigationLinks = [
        footer?.content_footer_link_marketplace && {
            label: 'Marketplace',
            href: footer.content_footer_link_marketplace,
        },
        footer?.content_footer_link_municipio && {
            label: 'Município',
            href: footer.content_footer_link_municipio,
        },
        footer?.content_footer_link_bairro_comercial && {
            label: 'Bairro Comercial',
            href: footer.content_footer_link_bairro_comercial,
        },
        footer?.content_footer_link_privacidade && {
            label: translations.privacy,
            href: footer.content_footer_link_privacidade,
        },
        footer?.content_footer_link_cookies && {
            label: translations.cookies,
            href: footer.content_footer_link_cookies,
        },
    ].filter(Boolean) as { label: string; href: string }[];

    const hasFooterNavigationLinks = footerNavigationLinks.length > 0;

    const hasFooterPrivacyLink = !!footer?.content_footer_link_privacidade;
    const hasFooterCookiesLink = !!footer?.content_footer_link_cookies;

    useEffect(() => {
        fetchFooter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasPolicyPageContent = (pageKey: string): boolean => {
        const page = getPage(pageKey);
        if (!page) {
            return false;
        }

        if (typeof page === 'string' && page.trim() !== '') {
            return true;
        }

        return false;
    };

    return (
        <footer
            className={
                // themes
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles.footer}`
            }
            data-testid="footer">
            {mode === 'short' ? (
                <>
                    <div className="container-fluid">
                        <hr className="m-0" />
                    </div>
                    <Container classes="py-4 py-lg-3">
                        <div className="d-block d-lg-flex justify-content-between text-center">
                            <div className="align-self-center py-4 py-lg-3">
                                <Typography classes="p-0 m-0 text-body-secondary">
                                    <>
                                        {data?.copyright && (
                                            <>
                                                {data?.copyright}
                                                <br />
                                            </>
                                        )}

                                        {translations.allRightsReserved}
                                    </>
                                </Typography>
                            </div>
                            <FinancingLogo src={fundingLogoSrc} />
                            <div className="d-inline-block align-self-center d-lg-block py-4 py-lg-3 pe-3 pe-lg-0">
                                {/* #TODO: confirm URL */}
                                <a
                                    href="https://www.consumidor.gov.pt/livro-de-reclamacoes.aspx"
                                    target="_blank"
                                    rel="noreferrer">
                                    <img className="mx-2" src={complaintsBookLogoSrc} alt={translations.livroReclamacoesAlt} />
                                </a>
                            </div>
                        </div>
                    </Container>
                </>
            ) : (
                <>
                    <Container classes="pt-5 pb-4 py-md-5">
                        <div className="d-block d-md-flex justify-content-between py-0 py-md-5 text-center">
                            <div className="d-flex flex-column my-3">
                                {footerLogoSrc && (
                                    <img className={`${styles['brand-logo']} mt-2 mb-5`} src={footerLogoSrc} alt="" />
                                )}
                                {socialNetworks && socialNetworks.length > 0 && (
                                    <ul className={`nav d-inline-flex ${styles['social-links']}`}>
                                        {socialNetworks.map(
                                            (socialNetwork, index) =>
                                                socialNetwork.url && (
                                                    <li className="mb-2" key={index}>
                                                        <Anchor
                                                            href={socialNetwork.url}
                                                            mode={AnchorMode.none}
                                                            target="_blank"
                                                            rel="noopener noreferrer">
                                                            <RoundIcon
                                                                icon={socialNetwork.icon}
                                                                variant={RoundIconVariants.variant1}
                                                            />
                                                        </Anchor>
                                                    </li>
                                                )
                                        )}
                                    </ul>
                                )}
                            </div>
                            {hasFooterNavigationLinks ||
                            hasPolicyPageContent('refunds') ||
                            hasPolicyPageContent('terms-and-conditions') ||
                            hasPolicyPageContent('cookies-policy') ||
                            hasPolicyPageContent('privacy-policy') ? (
                                <div className="my-3">
                                    <ul className="nav flex-column">
                                        {footerNavigationLinks.map((link, index) => (
                                            <li className="my-2" key={`footer-nav-${index}`}>
                                                <Typography fontSize="14">
                                                    <Anchor href={link.href} mode={AnchorMode.none}>
                                                        {link.label}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        ))}
                                        {hasPolicyPageContent('refunds') && (
                                            <li className="my-2">
                                                <Typography fontSize="14">
                                                    <Anchor
                                                        href={SITE_URLS.policy.replace(':pageKey', 'refunds')}
                                                        mode={AnchorMode.none}>
                                                        {translations.refunds}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                        {hasPolicyPageContent('terms-and-conditions') && (
                                            <li className="my-2">
                                                <Typography fontSize="14">
                                                    <Anchor
                                                        href={SITE_URLS.policy.replace(':pageKey', 'terms-and-conditions')}
                                                        mode={AnchorMode.none}>
                                                        {translations.termsConditions}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                        {hasPolicyPageContent('privacy-policy') && !hasFooterPrivacyLink && (
                                            <li className="my-2">
                                                <Typography fontSize="14">
                                                    <Anchor
                                                        href={SITE_URLS.policy.replace(':pageKey', 'privacy-policy')}
                                                        mode={AnchorMode.none}>
                                                        {translations.privacy}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                        {hasPolicyPageContent('cookies-policy') && !hasFooterCookiesLink && (
                                            <li className="my-2">
                                                <Typography fontSize="14">
                                                    <Anchor
                                                        href={SITE_URLS.policy.replace(':pageKey', 'cookies-policy')}
                                                        mode={AnchorMode.none}>
                                                        {translations.cookies}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            ) : (
                                ''
                            )}
                            <div className="my-3">
                                <ul className="nav flex-column">
                                    {(footerAddress || data?.location) && (
                                        <li className="my-2 d-none d-md-block">
                                            <Typography classes="p-0 m-0 text-body-secondary" fontSize="14">
                                                <>
                                                    {footerAddress ? (
                                                        <>
                                                            {footerAddressParts[0]}
                                                            {footerAddressParts.length > 1 && (
                                                                <>
                                                                    <br />
                                                                    {footerAddressParts.slice(1).join(',').trim()}
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {data?.location}
                                                            <br />
                                                            {data?.locationComplement}
                                                        </>
                                                    )}
                                                </>
                                            </Typography>
                                        </li>
                                    )}
                                    {(footerEmail || data?.email) && (
                                        <li className="my-2">
                                            <Typography classes="p-0 m-0 text-body-secondary" fontSize="14">
                                                {footerEmail || data?.email ? (
                                                    <Anchor mailTo={footerEmail || data?.email} mode={AnchorMode.none}>
                                                        {footerEmail || data?.email}
                                                    </Anchor>
                                                ) : null}
                                            </Typography>
                                        </li>
                                    )}
                                    {(footerPhone || data?.phone) && (
                                        <li className="my-2">
                                            <Typography classes="p-0 m-0 text-body-secondary" fontSize="14">
                                                <>
                                                    {footerPhone || data?.phone}
                                                    <br />
                                                    <Typography
                                                        classes="d-none d-md-block"
                                                        tagVariant={TypographyTagVariants.span}
                                                        fontSize="12">
                                                        {footerCallInfo}
                                                    </Typography>
                                                </>
                                            </Typography>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </Container>
                    <div className="container-fluid">
                        <hr className="m-0" />
                    </div>
                    <Container classes="py-4 py-lg-3">
                        <div className="d-block d-lg-flex justify-content-between text-center">
                            <div className="align-self-center py-4 py-lg-3">
                                <Typography classes="p-0 m-0 text-body-secondary" fontSize="14">
                                    <>
                                        {data?.copyright && (
                                            <>
                                                {data?.copyright}
                                                <br />
                                            </>
                                        )}
                                        {translations.allRightsReserved}
                                    </>
                                </Typography>
                            </div>
                            <FinancingLogo src={fundingLogoSrc} />
                            <div className="d-inline-block align-self-center d-lg-block py-4 py-lg-3 pe-3 pe-lg-0">
                                {/* #TODO: confirm URL */}
                                <a
                                    href="https://www.consumidor.gov.pt/livro-de-reclamacoes.aspx"
                                    target="_blank"
                                    rel="noreferrer">
                                    <img className="mx-2" src={complaintsBookLogoSrc} alt={translations.livroReclamacoesAlt} />
                                </a>
                            </div>
                        </div>
                    </Container>
                </>
            )}
        </footer>
    );
};
